import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MS } from '@/modules/shared/constants/time'
import { axiosInstance } from '@/lib/axios'
import type { DriverModel } from '@/app/models'

type Props = {
  companyId: string | number | undefined
  driverId: string | number | undefined
  withPersonalData?: boolean
  enabled?: boolean
}

/**
 * Fetches a single driver by id. Replaces the common
 *   `getDriver({ companyId, driverId, withPersonalData })` imperative pattern
 * across view bars / charts / edit pages — react-query then dedupes
 * concurrent calls, caches across route changes, and avoids the "driver info
 * refetches every time I switch tabs" class of bugs.
 */
export function useQueryDriver(props: Props) {
  const {
    companyId,
    driverId,
    withPersonalData = true,
    enabled = true,
  } = props

  return useQuery<DriverModel | null>({
    queryKey: ['driver', String(companyId), String(driverId), withPersonalData],
    enabled: enabled && !!companyId && !!driverId,
    staleTime: MINUTE_IN_MS * 5,
    // 403 = kierowca spoza grup użytkownika; ponawianie nic nie zmieni.
    retry: (failureCount, error) =>
      !(axios.isAxiosError(error) && error.response?.status === 403) && failureCount < 3,
    queryFn: async () => {
      const res = await axiosInstance.get(
        `api/${companyId}/driver/${driverId}?withPersonalData=${withPersonalData}`,
      )
      return res.data
    },
  })
}
